import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { io as ioClient } from "socket.io-client";
import api, { API_ORIGIN, assetUrl } from "../api";
import {
  Search, Calendar, Clock, MoreVertical, Check, X, Eye, Pencil, Layers, CheckCircle2, XCircle, Download, ArrowUpRight,
} from "lucide-react";
import UserLayout from "../components/Userlayout";
import SafeImage from "../components/SafeImage";
import {
  money, fmtDate, customerQuoteState, pageWindow,
} from "../quoteUtils";

// The quote the customer may see for a row from /requests/mine.
// latestQuotation is the newest version; it's a draft while the admin is editing,
// in which case we hide it (state "updating").
const visibleQuote = (r) => {
  const state = customerQuoteState(r);
  if (state === "awaiting" || state === "updating") return null;
  const q = r.latestQuotation;
  return q && q.status !== "draft" ? q : null;
};

export default function Quotes() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showDateMenu, setShowDateMenu] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [acting, setActing] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const menuRef = useRef(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!previewImage) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setPreviewImage(null);
    };
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = oldOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [previewImage]);

  const formatDuration = (startDate, endDate = new Date()) => {
    if (!startDate) return "—";

    const start = new Date(startDate);
    const end = new Date(endDate);

    let diff = Math.max(0, end.getTime() - start.getTime());

    const totalMinutes = Math.floor(diff / 60000);

    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    const parts = [];

    if (days > 0) {
      parts.push(`${days} day${days !== 1 ? "s" : ""}`);
    }

    if (hours > 0) {
      parts.push(`${hours} hr${hours !== 1 ? "s" : ""}`);
    }

    parts.push(`${minutes} min`);

    return parts.join(" ");
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/requests/mine");
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // live: refresh when the supplier sends/updates a quote
  useEffect(() => {
    if (!user?.id) return;
    const socket = ioClient(API_ORIGIN, { transports: ["websocket", "polling"], auth: { token: localStorage.getItem("token") } });
    socket.emit("join", `user:${user.id}`);
    socket.on("quotes:update", load);
    socket.on("notification:new", (n) => { if (n?.type === "quote_sent") load(); });
    return () => socket.disconnect();
  }, [user?.id, load]);

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(null); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const rows = useMemo(() => requests.map((r) => ({ ...r, _state: customerQuoteState(r), _quote: visibleQuote(r) })), [requests]);

  // "Pending" = not decided yet: awaiting a quote, quote sent, being updated, or revision asked for
  const PENDING_STATES = ["awaiting", "sent", "updating", "revision_requested"];
  const matchesTab = (r, key) => (key === "all" ? true : key === "pending" ? PENDING_STATES.includes(r._state) : r._state === key);

  const counts = useMemo(() => {
    const c = { all: rows.length, pending: 0, approved: 0, declined: 0 };
    rows.forEach((r) => {
      if (PENDING_STATES.includes(r._state)) c.pending += 1;
      else if (c[r._state] !== undefined) c[r._state] += 1;
    });
    return c;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const filtered = useMemo(() => {
    let list = rows.filter((r) => matchesTab(r, stateFilter));
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        [r.requestId, r.quoteId, r.partRequired, r.partNumber, r.notes, r._quote?.parts?.[0]?.name]
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q))
      );
    }
    const dateOf = (r) => new Date(r._quote ? r.quotedAt : r.createdAt);
    if (dateFrom) list = list.filter((r) => dateOf(r) >= new Date(dateFrom));
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter((r) => dateOf(r) <= to);
    }
    return list;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, stateFilter, search, dateFrom, dateTo]);

  useEffect(() => { setPage(1); }, [search, stateFilter, dateFrom, dateTo, limit]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pageItems = filtered.slice((page - 1) * limit, page * limit);

  const respond = async (r, status) => {
    setMenuOpen(null);
    const q = r._quote;
    if (!q) return;
    const verb = status === "approved" ? "approve" : "decline";
    if (!window.confirm(`Are you sure you want to ${verb} quote ${q.quoteId} for ${money(q.pricing?.total)}?`)) return;
    setActing(r._id);
    try {
      await api.patch(`/quotations/${q._id}/status`, { status });
      await load();
    } catch (e) {
      alert(e.response?.data?.message || `Could not ${verb} the quote.`);
    } finally {
      setActing(null);
    }
  };

  const exportQuotes = () => {
    const escapeCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const csv = [
      ["Quote ID", "Request ID", "Part", "Part Number", "Quote Date", "Response Time", "Status", "Price (AUD)"],
      ...filtered.map((r) => {
        const q = r._quote;
        return [
          q?.quoteId || "", r.requestId || "", q?.parts?.[0]?.name || r.partRequired || "Part request",
          q?.parts?.[0]?.partNumber || r.partNumber || "", fmtDate(q ? r.quotedAt : r.createdAt),
          q ? formatDuration(r.createdAt, r.quotedAt) : formatDuration(r.createdAt, now),
          r._state, q?.pricing?.total ?? "",
        ];
      }),
    ].map((line) => line.map(escapeCell).join(",")).join("\r\n");
    const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "my-quotes.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const thumb = (r) => r._quote?.parts?.[0]?.image || r.partPhotos?.[0] || r.vehicleImage || "";

  return (
    <UserLayout>

      <style>{`
        .agp-quotes-page { overflow-x: clip; }
        .agp-quotes-search-panel { box-shadow: 0 3px 14px rgba(21, 30, 46, .07); }
        .agp-quotes-search-input:focus, .agp-quotes-date-button:focus-visible { outline: 2px solid #3b6cb7; outline-offset: 2px; }
        .agp-quote-request-badge {
          display: inline-flex; align-items: center; width: fit-content;
          max-width: 100%; padding: 7px 12px; border-radius: 8px;
          background: #e9edff; border: 1px solid #cbd5ff;
          color: #293bba; font-size: 12px; font-weight: 800;
          letter-spacing: .02em; overflow-wrap: anywhere;
        }
        .agp-quote-card { border: 1px solid #e5e9f1; border-radius: 12px !important; }
        .agp-quote-card:hover { border-color: #c6d2ee; }
        @media (max-width: 767px) {
          .agp-quotes-page .agp-page-wrap { width: calc(100% - 24px); }
          .agp-quotes-search-panel { padding: 12px !important; border-radius: 14px !important; }
          .agp-quotes-filter-grid { display: grid !important; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important; gap: 12px !important; }
          .agp-quotes-search-field { grid-column: 1 / -1; }
          .agp-quotes-search-input { height: 43px !important; padding: 0 38px 0 12px !important; background: #fff !important; border: 1px solid #d1d5df !important; border-radius: 9px !important; font-size: 12px !important; }
          .agp-quotes-search-icon { left: auto !important; right: 12px !important; }
          .agp-quotes-date-label { display: block !important; font-size: 10px; font-weight: 600; color: #6d7280; letter-spacing: .025em; margin-bottom: 5px; }
          .agp-quotes-date-button { width: 100%; height: 40px !important; padding: 0 10px !important; font-size: 12px !important; border: 1px solid #d1d5df !important; border-radius: 9px !important; background: #fff !important; justify-content: space-between; gap: 4px !important; }
          .agp-quotes-date-button svg { order: 2; flex-shrink: 0; }
          .agp-quotes-date-button span { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .agp-quotes-date-popover { left: 0 !important; right: auto !important; width: min(290px, calc(100vw - 48px)) !important; }
          .agp-quotes-export { align-self: end; height: 40px !important; border-radius: 9px !important; font-size: 12px !important; }
          .agp-quote-tabs { display: grid !important; grid-template-columns: repeat(2,minmax(0,1fr)) !important; gap: 8px !important; background: transparent !important; overflow: visible !important; }
          .agp-quote-tabs > button { min-width: 0; margin: 0 !important; height: 43px !important; font-size: 12px !important; gap: 7px !important; background: #fff; border: 1px solid #e4e7ed; box-shadow: 0 1px 3px rgba(15,23,42,.04); }
          .agp-quote-tabs > button[aria-selected="true"] { background: #0e315e !important; border-color: #0e315e !important; }
          .agp-quote-tabs > button svg { width: 15px; height: 15px; flex-shrink: 0; }
          .agp-quote-tabs > button span { font-size: 10px; }
          .agp-quote-card { padding: 12px !important; border-radius: 14px !important; box-shadow: 0 2px 9px rgba(15,23,42,.05); }
          .agp-quote-card-grid { position: relative; display: grid !important; grid-template-columns: 82px minmax(0,1fr) !important; gap: 10px 12px !important; align-items: start !important; padding: 0 !important; }
          .agp-quote-checkbox { display: none !important; }
          .agp-quote-thumb { grid-column: 1; grid-row: 1; height: 87px !important; width: 82px !important; border-radius: 10px !important; border: 1px solid #e4e8f0; background: #f6f7fc; }
          .agp-quote-thumb img { padding: 5px !important; }
          .agp-quote-description { grid-column: 2; grid-row: 1; min-width: 0; padding-right: 36px; }
          .agp-quote-ids { gap: 0 !important; }
          .agp-quote-request-badge { font-size: 11px; padding: 5px 8px; border-radius: 7px; }
          .agp-quote-description h3 { font-size: 16px !important; line-height: 1.24 !important; margin-top: 8px !important; overflow-wrap: anywhere; }
          .agp-quote-description p { font-size: 11px !important; line-height: 1.3; margin-top: 4px !important; overflow-wrap: anywhere; }
          .agp-quote-date { grid-column: 1; grid-row: 2; }
          .agp-quote-time { grid-column: 2; grid-row: 2; }
          .agp-quote-meta { background: #f5f8fd !important; padding: 10px !important; border-radius: 9px !important; min-width: 0; min-height: 69px; }
          .agp-quote-meta > div { gap: 6px !important; align-items: flex-start; }
          .agp-quote-meta svg { width: 14px; height: 14px; margin-top: 1px !important; flex-shrink: 0; }
          .agp-quote-meta span { display: block; font-size: 9px !important; line-height: 1.3; white-space: normal; }
          .agp-quote-meta strong { font-size: 11px !important; line-height: 1.4; margin-top: 5px !important; overflow-wrap: anywhere; }
          .agp-quote-price { grid-column: 1 / -1; grid-row: 3; display: flex; justify-content: space-between; align-items: center; gap: 8px; border-top: 1px solid #edf0f5; padding: 10px 2px 0; min-width: 0; }
          .agp-quote-price span { font-size: 10px !important; }
          .agp-quote-price strong { font-size: 16px !important; margin-top: 0 !important; text-align: right; overflow-wrap: anywhere; }
          .agp-quote-actions { position: absolute !important; right: 0; top: 0; z-index: 8; }
          .agp-quote-actions > button { width: 32px; height: 32px; border-radius: 9px; background: white; }
          .agp-quote-actions > div { z-index: 90; }
        }
        @media (max-width: 360px) {
          .agp-quote-card-grid { grid-template-columns: 72px minmax(0,1fr) !important; column-gap: 9px !important; }
          .agp-quote-thumb { width: 72px !important; height: 80px !important; }
          .agp-quote-description h3 { font-size: 14px !important; }
          .agp-quote-meta { padding: 8px !important; }
          .agp-quote-meta svg { display: none; }
        }

        /* 2026 modern request cards: desktop and mobile */
        .agp-quote-card { border: 1px solid #e4eaf2 !important; border-radius: 16px !important; box-shadow: 0 3px 16px rgba(16,38,73,.055); overflow: visible; }
        .agp-quote-card:hover { border-color: #b9cce7 !important; box-shadow: 0 10px 24px rgba(16,38,73,.10) !important; transform: translateY(-1px); }
        .agp-quote-request-badge { background: #e7efff; color: #174aa2; border-color: #c9dcff; border-radius: 999px; padding: 6px 13px; font-weight: 800; }
        .agp-quote-thumb { cursor: zoom-in; padding: 0; }
        .agp-quote-thumb:focus-visible { outline: 3px solid #176eff; outline-offset: 3px; }
        .agp-quote-thumb { border-radius: 12px !important; background: #f3f6fb !important; }
        .agp-quote-description h3 { margin-top: 9px; letter-spacing: -.025em; }
        .agp-quote-meta { background: #f5f8fc !important; border: 1px solid #edf2f9; }
        .agp-quote-meta > div > div { min-width: 0; }
        .agp-quote-price { min-width: 0; }
        .agp-quote-price-content { min-width: 0; }
        .agp-quote-price strong { color: #078847; overflow-wrap: anywhere; }
        .agp-quote-view { display: none; align-items: center; justify-content: center; gap: 7px; border: 0; border-radius: 9px; background: #173d80; color: #fff; font-weight: 700; cursor: pointer; }
        .agp-quote-view:hover { background: #102d61; }
        .agp-quote-view:focus-visible, .agp-quote-actions > button:focus-visible { outline: 2px solid #3975ed; outline-offset: 3px; }
        @media (min-width: 768px) {
          .agp-quote-card { padding: 18px 20px !important; }
          .agp-quote-card-grid { grid-template-columns: 22px 92px minmax(165px,1.7fr) minmax(130px,1fr) minmax(130px,1fr) minmax(125px,1fr) 34px !important; gap: 14px !important; }
          .agp-quote-meta { padding: 14px !important; border-radius: 12px !important; }
          .agp-quote-meta > div { gap: 9px !important; }
          .agp-quote-price { display: block; }
          .agp-quote-price strong { display: block; font-size: 16px !important; }
        }
        @media (min-width: 768px) and (max-width: 1120px) {
          .agp-quote-card-grid { grid-template-columns: 20px 80px minmax(130px,1.5fr) minmax(105px,1fr) minmax(105px,1fr) minmax(110px,1fr) 30px !important; gap: 9px !important; }
          .agp-quote-meta { padding: 10px !important; }
          .agp-quote-description h3 { font-size: 17px !important; }
          .agp-quote-meta svg { display: none; }
          .agp-quote-meta strong { font-size: 12px !important; }
        }
        @media (max-width: 767px) {
          .agp-quote-card { padding: 14px !important; border-radius: 17px !important; }
          .agp-quote-card-grid { display: grid !important; grid-template-columns: repeat(12, minmax(0, 1fr)) !important; column-gap: 12px !important; row-gap: 12px !important; padding: 0 !important; }
          .agp-quote-thumb { grid-column: 1 / 4 !important; grid-row: 1 !important; width: 76px !important; height: 80px !important; border: 1px solid #e6edf7 !important; }
          .agp-quote-description { grid-column: 4 / -1 !important; grid-row: 1 !important; padding-right: 31px !important; align-self: center; }
          .agp-quote-request-badge { font-size: 11px !important; padding: 5px 10px !important; }
          .agp-quote-description h3 { font-size: 16px !important; line-height: 1.24 !important; margin-top: 7px !important; }
          .agp-quote-description p { font-size: 11px !important; margin-top: 4px !important; }
          .agp-quote-actions { right: 0 !important; top: 0 !important; }
          .agp-quote-actions > button { border-color: #e3eaf3; background: #f8faff; }
          .agp-quote-date { grid-column: 1 / 7 !important; grid-row: 2 !important; }
          .agp-quote-time { grid-column: 7 / -1 !important; grid-row: 2 !important; }
          .agp-quote-meta { padding: 11px 9px !important; border-radius: 10px !important; min-height: 75px; }
          .agp-quote-meta > div { display: block !important; }
          .agp-quote-meta svg { display: none !important; }
          .agp-quote-meta span { font-size: 9px !important; line-height: 1.25; color: #64748b !important; }
          .agp-quote-meta strong { margin-top: 7px !important; display: block; font-size: 12px !important; line-height: 1.35 !important; }
          .agp-quote-price { grid-column: 1 / -1 !important; grid-row: 3 !important; padding: 12px 0 0 !important; margin-top: 1px; border-top: 1px solid #eaf0f6; display: flex !important; align-items: center; justify-content: space-between; gap: 10px; }
          .agp-quote-price span { font-size: 9px !important; }
          .agp-quote-price strong { display: block; text-align: left !important; font-size: 17px !important; margin-top: 3px !important; }
          .agp-quote-view { display: inline-flex; flex: 0 0 auto; min-height: 38px; padding: 0 12px; font-size: 11px; white-space: nowrap; }
        }
        @media (max-width: 360px) {
          .agp-quote-card { padding: 11px !important; }
          .agp-quote-card-grid { grid-template-columns: repeat(12, minmax(0, 1fr)) !important; column-gap: 9px !important; }
          .agp-quote-thumb { width: 65px !important; height: 74px !important; }
          .agp-quote-description h3 { font-size: 14px !important; }
          .agp-quote-meta strong { font-size: 11px !important; }
          .agp-quote-view { padding: 0 9px; font-size: 10px; }
          .agp-quote-price strong { font-size: 14px !important; }
        }
      `}</style>
      <main className="agp-quotes-page min-h-[calc(100vh-92px)] bg-[#f7f7f9] pb-14">
        <div className="agp-page-wrap pt-5 md:pt-8">
          <section className="agp-quotes-search-panel rounded-[14px] border border-[#e7e9ef] bg-white p-5 md:p-6">
            <div className="agp-quotes-filter-grid grid items-end gap-4 md:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)_150px]">
              <div className="agp-quotes-search-field relative">
                <Search size={18} className="agp-quotes-search-icon pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[#737987]" />
                <input aria-label="Search quotes" value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search by Quote ID, Item or Part Number..." className="agp-quotes-search-input h-[44px] w-full rounded-[9px] border border-[#d1d5df] bg-white pl-11 pr-4 text-[13px] outline-none" />
              </div>
              <div className="relative">
                <label className="agp-quotes-date-label hidden">SEARCH BY DATE</label>
                <button type="button" aria-expanded={showDateMenu} onClick={()=>setShowDateMenu((v)=>!v)} className="agp-quotes-date-button flex h-[44px] w-full items-center gap-3 rounded-[9px] border border-[#d1d5df] bg-white px-4 text-left text-[13px] text-[#737780]">
                  <Calendar size={17} /><span>{dateFrom || dateTo ? `${dateFrom || 'Any'} – ${dateTo || 'Any'}` : 'Select Date'}</span>
                </button>
                {showDateMenu && <div className="agp-quotes-date-popover absolute right-0 top-[calc(100%+7px)] z-30 w-64 rounded-xl border bg-white p-4 shadow-xl"><label className="mb-3 block text-xs">From<input type="date" value={dateFrom} onChange={(e)=>setDateFrom(e.target.value)} className="mt-1 w-full rounded-lg border p-2"/></label><label className="block text-xs">To<input type="date" value={dateTo} onChange={(e)=>setDateTo(e.target.value)} className="mt-1 w-full rounded-lg border p-2"/></label><div className="mt-3 flex justify-between"><button type="button" onClick={()=>{setDateFrom('');setDateTo('');setShowDateMenu(false);}} className="text-xs font-semibold text-slate-600">Clear</button><button type="button" onClick={()=>setShowDateMenu(false)} className="text-xs font-semibold text-blue-600">Done</button></div></div>}
              </div>
              <button type="button" onClick={exportQuotes} disabled={loading || filtered.length === 0} className="agp-quotes-export flex h-[44px] items-center justify-center gap-2 rounded-[9px] bg-[#27246d] px-4 text-[13px] font-semibold text-white disabled:opacity-50"><Download size={16}/>Export</button>
            </div>
          </section>

          <section role="tablist" className="agp-quote-tabs mt-4 grid overflow-hidden rounded-[10px] bg-white md:grid-cols-4">
            {[["all","All Quotes",Layers,""],["pending","Pending",CheckCircle2,""],["approved","Approved",Clock,""],["declined","Declined",XCircle,"text-[#e5303b]"]].map(([k,label,Icon,iconCls])=>{
              const active = stateFilter===k;
              return <button key={k} role="tab" aria-selected={active} onClick={()=>setStateFilter(k)} className={`m-[10px] flex h-[50px] items-center justify-center gap-3 rounded-[8px] text-[15px] transition ${active?'bg-[#0e315e] font-semibold text-white':'text-[#71757d] hover:bg-slate-50'}`}>
                <Icon size={18} className={active ? "" : iconCls} />{label}{counts[k] ? <span className="text-[11px] opacity-70">{counts[k]}</span>:null}
              </button>;
            })}
          </section>

          <section className="mt-4 space-y-4">
            {loading && <div className="rounded-xl bg-white py-16 text-center text-slate-400">Loading…</div>}
            {!loading && pageItems.map((r)=>{
              const q=r._quote; const state=r._state; const img=thumb(r); const itemName=q?.parts?.[0]?.name || r.partRequired || r.notes || 'Part request'; const itemSub=q?.parts?.[0]?.partNumber || r.partNumber || ''; const dateToShow=q ? r.quotedAt : r.createdAt;
              return <article key={r._id} onClick={()=>navigate(`/quotes/${r._id}`)} className="agp-quote-card relative cursor-pointer rounded-[4px] bg-white px-5 py-3 transition hover:shadow-md">
                <div className="agp-quote-card-grid grid items-center gap-4 md:grid-cols-[24px_104px_1.65fr_190px_190px_170px_34px]">
                  <span className="agp-quote-checkbox h-5 w-5 rounded-[6px] border border-[#eceef1] bg-[#fafafa]" />
                  <button type="button" className="agp-quote-thumb grid h-[94px] place-items-center overflow-hidden rounded-[8px] bg-[#f5f3ff]" aria-label={`View full image of ${itemName}`} onClick={(e) => { e.stopPropagation(); if (img) setPreviewImage({ src: assetUrl(img), alt: itemName }); }}><SafeImage src={img ? assetUrl(img) : ""} alt={itemName} className="h-full w-full object-contain p-3" /></button>
                  <div className="agp-quote-description"><div className="agp-quote-ids flex items-center gap-3 text-[13px] font-semibold text-[#233249]"><span className="agp-quote-request-badge">{r.requestId || "Request"}</span></div><h3 className="mt-3 text-[21px] font-semibold text-[#122039]">{itemName}</h3><p className="mt-1 text-[13px] text-[#858891]">{itemSub || 'Part request'}</p></div>
                  <div className="agp-quote-meta agp-quote-date rounded-[9px] bg-[#f5f8fd] px-6 py-4"><div className="flex gap-3"><Calendar size={18} className="mt-1 text-[#176eff]"/><div><span className="text-[10px] uppercase text-[#7a808b]">Quote Date</span><strong className="mt-1 block text-[14px] text-[#172238]">{fmtDate(dateToShow)}</strong></div></div></div>
                  <div className="agp-quote-meta agp-quote-time rounded-[9px] bg-[#f5f8fd] px-6 py-4"><div className="flex gap-3"><Clock size={18} className="mt-1 text-[#176eff]"/><div><span className="text-[10px] uppercase text-[#7a808b]">Response Time</span><strong className="mt-1 block text-[14px] text-[#172238]">{q ? formatDuration(r.createdAt,r.quotedAt) : formatDuration(r.createdAt,now)}</strong></div></div></div>
                  <div className="agp-quote-price"><div className="agp-quote-price-content"><span className="text-[10px] uppercase text-[#7a808b]">Quote Price</span><strong className="mt-2 block text-[15px] text-[#06953a]">{q ? `${money(q.pricing?.total)} AUD` : 'Awaiting Quote'}</strong></div><button type="button" className="agp-quote-view" onClick={(e)=>{e.stopPropagation(); navigate(`/quotes/${r._id}`);}}>View Details <ArrowUpRight size={16}/></button></div>
                  <div className="agp-quote-actions relative" onClick={(e)=>e.stopPropagation()}><button onClick={()=>setMenuOpen(menuOpen===r._id?null:r._id)} className="grid h-8 w-8 place-items-center rounded-lg border text-slate-500"><MoreVertical size={17}/></button>{menuOpen===r._id && <div ref={menuRef} className="absolute right-0 top-10 z-40 w-48 rounded-xl border bg-white py-2 shadow-xl">{q && state==='sent' && <><button onClick={()=>respond(r,'approved')} disabled={acting===r._id} className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"><Check size={15} className="text-green-600"/>Approve Quote</button><button onClick={()=>respond(r,'declined')} disabled={acting===r._id} className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-slate-50 disabled:opacity-50"><X size={15}/>Decline Quote</button><button onClick={()=>navigate(`/quotes/${r._id}`, { state: { revise: true } })} className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50"><Pencil size={15}/>Revise Quote</button></>}{q && state!=='sent' && <button onClick={()=>navigate(`/quotes/${r._id}`)} className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50"><Eye size={15}/>View Quote</button>}{!q && <button onClick={()=>navigate(`/quotes/${r._id}`)} className="flex w-full items-center gap-2 px-4 py-2 text-sm hover:bg-slate-50"><Eye size={15}/>View Request</button>}</div>}</div>
                </div>
              </article>;
            })}
            {!loading && pageItems.length===0 && <div className="rounded-xl bg-white py-16 text-center text-slate-400">No quotes match your filters.</div>}
          </section>

          {totalPages>1 && <div className="mt-6 flex justify-center gap-2">{pageWindow(page,totalPages).map((p)=><button key={p} onClick={()=>setPage(p)} className={`h-9 w-9 rounded-lg text-sm ${p===page?'bg-[#0e315e] text-white':'bg-white text-slate-600'}`}>{p}</button>)}</div>}
        </div>
      </main>
      {previewImage && createPortal(
        <div role="dialog" aria-modal="true" aria-label={`Image preview: ${previewImage.alt}`} className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/90 p-4 sm:p-8" onClick={() => setPreviewImage(null)}>
          <button type="button" autoFocus aria-label="Close image preview" className="absolute right-4 top-4 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/15 text-white hover:bg-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white" onClick={(e) => { e.stopPropagation(); setPreviewImage(null); }}><X size={25} /></button>
          <img src={previewImage.src} alt={previewImage.alt} className="max-h-[90dvh] max-w-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>,
        document.body
      )}
    </UserLayout>
  );
}
